/**
 * Lecteur d'une animation PUBLIÉE (lot 4) : page publique `/animation/$slug`,
 * page d'une SKILLCARD, fenêtre « Cours » du player.
 *
 *   - version servie par `get_animation` : sans accès à la SKILLCARD, le
 *     document gratuit et la liste des scènes verrouillées (carte
 *     « Débloquer » à la fin) ;
 *   - garde de version : une app trop ancienne pour ce document affiche
 *     « Mets à jour l'application » au lieu d'une animation cassée ;
 *   - mesure : une lecture = `page_view /animations/<slug>` au premier
 *     « Lancer », `/animations/<slug>/fin` quand elle est allée au bout ;
 *   - « Ouvrir l'outil » : le vrai outil de la Boîte à outils.
 */

import { useMemo, useState } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import { readPublishedJraf } from '@jeraisonne/animation';
import { cn } from '@jeraisonne/ui';
import { AnimationPlayer } from '@jeraisonne/ui/components/animations/animation-player';
import { useSpeakerAvatars } from '@/lib/animations/speakers';
import { isToolId, toolById, type ToolId } from '@/features/toolbox/catalog';
import { ToolboxSheet } from '@/features/toolbox/toolbox-sheet';
import { trackAppEvent } from '@/lib/analytics/track';
import { useAuth } from '@/lib/auth';
import { usePublishedAnimation, type PublishedAnimation } from '@/lib/animations/published';
import { useAnimationSoundtrack } from '@/lib/animations/audio';
import { animationAssetUrl } from '@/lib/animations/voice';
import { isNativeApp } from '@/lib/platform';

/** Même largeur que le lecteur (9:16, borné par la hauteur de l'écran). */
const FRAME = 'mx-auto w-full max-w-[min(100%,max(260px,calc((100dvh-150px)*9/16)))]';

export function AnimationNotice({
  children,
  action,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
  tone?: 'neutral' | 'error';
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        FRAME,
        'border-ink-200 flex flex-col items-center gap-3 rounded-2xl border bg-white p-6 text-center',
      )}
    >
      <p className={cn('text-sm', tone === 'error' ? 'text-error-600' : 'text-ink-600')}>
        {children}
      </p>
      {action}
    </div>
  );
}

export function AnimationLoading() {
  return (
    <div
      data-testid="animation-loading"
      className={cn(FRAME, 'bg-ink-100 aspect-[9/16] animate-pulse rounded-2xl')}
    />
  );
}

/** Une app trop ancienne pour ce document : jamais une animation à moitié jouée. */
export function AnimationUpdateNotice() {
  const native = isNativeApp();
  return (
    <div
      role="status"
      data-testid="animation-update-notice"
      className={cn(
        FRAME,
        'border-primary-200 bg-primary-50 flex flex-col items-center gap-3 rounded-2xl border p-6 text-center',
      )}
    >
      <Sparkles className="text-primary-600 h-6 w-6" aria-hidden />
      <p className="font-display text-ink-900 text-base font-bold">Mets à jour l’application</p>
      <p className="text-ink-600 text-sm">
        {native
          ? 'Cette animation utilise des nouveautés que ta version de JeRaisonne ne connaît pas encore. Mets l’application à jour depuis ton store pour la voir.'
          : 'Cette animation utilise des nouveautés que ta version de l’application ne connaît pas encore. Recharge la page pour passer à la dernière.'}
      </p>
      {!native && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="bg-primary-600 flex min-h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold text-white active:scale-[0.98]"
        >
          <RefreshCw className="h-4 w-4" aria-hidden /> Recharger
        </button>
      )}
    </div>
  );
}

export function PublishedAnimationPlayer({
  data,
  onUnlock,
  onOpenTool,
}: {
  data: PublishedAnimation;
  /** carte « Débloquer » de la fin (sans : elle annonce la partie verrouillée sans bouton) */
  onUnlock?: () => void;
  /** outil ouvert par le parent (fenêtre « Cours ») ; sinon ici, dans la Boîte à outils */
  onOpenTool?: (toolId: ToolId) => void;
}) {
  const read = useMemo(() => readPublishedJraf(data.doc, data.runtime_min), [data]);
  const soundtrack = useAnimationSoundtrack();
  const speakerAvatars = useSpeakerAvatars(read.ok ? read.doc : null);
  const [tool, setTool] = useState<ToolId | null>(null);
  const toolDef = tool ? toolById(tool) : null;

  if (!read.ok) {
    return read.reason === 'update' ? (
      <AnimationUpdateNotice />
    ) : (
      <AnimationNotice tone="error">Cette animation est illisible pour le moment.</AnimationNotice>
    );
  }
  return (
    <>
      <AnimationPlayer
        doc={read.doc}
        locked={data.locked}
        onUnlock={data.locked.length > 0 ? onUnlock : undefined}
        onOpenTool={(id) => {
          if (!isToolId(id)) return;
          if (onOpenTool) onOpenTool(id);
          else setTool(id);
        }}
        soundtrack={soundtrack}
        speakerAvatars={speakerAvatars}
        assetUrl={animationAssetUrl}
        onStart={() => trackAppEvent('page_view', `/animations/${data.slug}`)}
        onComplete={() => trackAppEvent('page_view', `/animations/${data.slug}/fin`)}
      />
      {tool && toolDef && (
        <ToolboxSheet
          context={{ level: null, families: toolDef.families, linked: [tool] }}
          initialTool={tool}
          featuredTitle="Outil de l’animation"
          onClose={() => setTool(null)}
        />
      )}
    </>
  );
}

/** Charge la version publiée (`get_animation`) puis la joue. */
export function PublishedAnimationView({
  slug,
  onUnlock,
  onOpenTool,
}: {
  slug: string;
  onUnlock?: () => void;
  onOpenTool?: (toolId: ToolId) => void;
}) {
  const { session, loading } = useAuth();
  // session pas encore lue : pas d'appel anonyme suivi d'un second, connecté
  const query = usePublishedAnimation(slug, session?.user?.id ?? null, !loading);

  if (query.isPending) return <AnimationLoading />;
  if (query.error) {
    return (
      <AnimationNotice
        tone="error"
        action={
          <button
            type="button"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            className="bg-primary-600 min-h-11 rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-60"
          >
            {query.isFetching ? 'Chargement…' : 'Réessayer'}
          </button>
        }
      >
        Impossible de charger l’animation. Vérifie ta connexion et réessaie.
      </AnimationNotice>
    );
  }
  if (!query.data) {
    return <AnimationNotice>Cette animation n’est pas (ou plus) disponible.</AnimationNotice>;
  }
  return (
    <PublishedAnimationPlayer
      // une autre version ou un autre accès (connexion) : lecteur neuf
      key={`${query.data.version_no}-${query.data.access.interactive}`}
      data={query.data}
      onUnlock={onUnlock}
      onOpenTool={onOpenTool}
    />
  );
}
